/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-21 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { reduxForm, getFormValues, change, reset } from 'redux-form';
import { Button, Page, Layout, Card } from '@shopify/polaris';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';
import General from 'src/components/views/recommendation/predictive-bundle/general';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { confirmLeavePage } from 'src/helpers';
import history from 'src/routes/history';
import {
  buildInitialWidgetValues,
  buildWidgetValues,
  validateFromGeneral
} from 'src/helpers/bundle-helper';

import type { ComposedProps, State } from './index.type';

const GeneralWidgetItem = (props): Component<ComposedProps, State> => {
  const {
    i18n,
    formValues,
    widget,
    host,
    setStep,
    setWidget,
    updateField,
    handleSubmit,
    dirty,
    resetForm,
    moneyFormat,
    dirtyOnStep,
    currentStep,
    handleCloseFullscreenPage
  } = props;

  const [validateText, setValidateText] = useState('');
  const bundleProducts = _.get(formValues, 'bundleProducts', []);
  // Handle confirm leave page
  const handleConfirmLeavePage = event => {
    return confirmLeavePage(event, dirty, i18n);
  };

  // Handle action next
  const handleNext = data => {
    if (bundleProducts.length < 2) {
      setValidateText(
        i18n.translate('AdminHTML.PredictiveBundle.Validate.invalid_BundleProducts_Empty')
      );
      return;
    }
    if (validateFromGeneral(data, i18n)) {
      let settings = buildWidgetValues(data);
      settings = { ...widget, ...settings };
      setStep(1);
      setWidget(settings);
    }
  };

  useEffect(() => {
    if (bundleProducts.length >= 2 && !_.isEmpty(validateText)) {
      setValidateText('');
    }
  }, [bundleProducts.length]);

  useEffect(() => {
    // Handle when reload or leave page
    window.addEventListener('beforeunload', handleConfirmLeavePage);
    //  Handle when reload or leave page
    return () => {
      window.removeEventListener('beforeunload', handleConfirmLeavePage);
      resetForm();
    }
  }, []);

  const shouldConfirmWhenLeave =
    !_.isEmpty(_.filter(dirtyOnStep, (value, key) => key !== `step_${currentStep}` && value)) ||
    dirty;

  return (
    <div className="BC-Page PFS-PredictiveBundleItem">
      <FullscreenBar
        onAction={() => handleCloseFullscreenPage(!shouldConfirmWhenLeave)}
        title={i18n.translate('AdminHTML.PredictiveBundle.Item.step1Title')}
        actions={
          <Button primary onClick={handleSubmit(handleNext.bind(this))}>
            {i18n.translate('AdminHTML.Common.Action.next')}
          </Button>
        }
        progress={33.3}
      />
      <div style={{ marginTop: '2rem' }}>
        <Page>
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <div className="setup-content">
                  <General
                    formValues={formValues}
                    recommendationWidget={widget}
                    host={host}
                    validateText={validateText}
                    updateField={updateField}
                    moneyFormat={moneyFormat}
                  />
                </div>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </div>
      {/* Confirm before leaving */}
      <RouteLeavingGuard
        when={shouldConfirmWhenLeave}
        // Navigate function
        navigate={path => history.push(path)}
        shouldBlockNavigation={shouldConfirmWhenLeave}
        isAvailableFullscreen={true}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (id, data) => dispatch(change(`GeneralPredictiveBundleWidgetItemForm`, id, data)),
  resetForm: () => dispatch(reset('GeneralPredictiveBundleWidgetItemForm'))
});

const mapStateToProps = (state, props) => {
  const { widget } = props;
  const formValues = getFormValues('GeneralPredictiveBundleWidgetItemForm')(state);

  return {
    initialValues: buildInitialWidgetValues(widget),
    formValues: formValues || {},
    host: _.get(state.oauth, 'host', ''),
    moneyFormat: _.get(state.oauth, 'tenantApp.externalInfo.currency', 'USD')
  };
};

const GeneralPredictiveBundleWidgetItemForm = reduxForm({
  form: 'GeneralPredictiveBundleWidgetItemForm', // a unique identifier for this form
  enableReinitialize: true
})(GeneralWidgetItem);

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(GeneralPredictiveBundleWidgetItemForm)
);
